/**
 * SIGNALS-PRACTICE-LENS-3W curator practice assignment — data access.
 *
 * Every call in this file is a `supabase.functions.invoke("curate_signal_practice",
 * ...)`, and only two of that function's operations are used: the
 * get_signal_practices read and the include/clear decisions. The browser never
 * reads or writes the assignment tables and never calls the writer or reader
 * RPC: those are revoked from anon and authenticated, and the Edge Function is
 * where the capability is derived.
 *
 * No function here mints an event key. The key is a parameter, owned by the
 * session's controller, so re-issuing this exact call is a retry of the same
 * decision rather than a second one. Failures are classified by the accepted
 * curator error module, so a timeout, a relay failure and an HTTP status mean
 * exactly what they mean for a publication decision.
 */

import { supabase } from "@/auth/supabase-client";
import {
  ensureFreshSession,
  SUPABASE_EDGE_TIMEOUT_MS,
  withSupabaseTimeout,
} from "@/auth/session-guard";
import { toCuratorApiError } from "@/features/curator-queue/errors";
import {
  buildPracticeAssignmentBody,
  parsePracticeAssignmentResult,
  parseSignalPracticeAssignments,
  type PracticeAssignmentIntent,
  type PracticeWriteOutcome,
  type SignalPracticeAssignments,
} from "./practice-assignment";

const FUNCTION_NAME = "curate_signal_practice";

/**
 * The one network call. `invoke` resolves `{ data, error }` for transport and
 * HTTP failures, and `withSupabaseTimeout` throws when its timer wins; both are
 * funnelled into one classified CuratorApiError.
 */
async function invokePractice(body: Record<string, unknown>): Promise<unknown> {
  let result: { data: unknown; error: unknown };
  try {
    await ensureFreshSession();
    result = await withSupabaseTimeout(
      supabase.functions.invoke(FUNCTION_NAME, { body }),
      `${FUNCTION_NAME} ${String(body.operation)}`,
      SUPABASE_EDGE_TIMEOUT_MS
    );
  } catch (thrown) {
    throw await toCuratorApiError(thrown);
  }
  if (result.error) throw await toCuratorApiError(result.error);

  // successResponse wraps every payload as { data: ... }.
  const payload = result.data;
  if (payload !== null && typeof payload === "object" && "data" in payload) {
    return (payload as { data: unknown }).data;
  }
  return payload;
}

/**
 * Every peer's current human state for one signal. Called only after a curator
 * opens the control, and again after every accepted decision - never on modal
 * open. A malformed body is refused, not repaired.
 */
export async function getSignalPracticeAssignments(
  signalId: string
): Promise<SignalPracticeAssignments> {
  const data = await invokePractice({
    operation: "get_signal_practices",
    signal_id: signalId,
  });
  try {
    return parseSignalPracticeAssignments(data, signalId);
  } catch {
    throw await toCuratorApiError(
      new Error("The practice assignments could not be read. Try again.")
    );
  }
}

/**
 * Records one include or clear under a caller-owned event key.
 *
 * A 2xx whose body cannot be matched to this request resolves as `unreadable`
 * rather than throwing: the server DID accept the request, so it must not be
 * presented as a failure the curator might resend, and it must not be reported
 * as a specific outcome either. Either way the caller re-reads.
 */
export async function submitPracticeAssignment(
  intent: PracticeAssignmentIntent,
  eventKey: string
): Promise<PracticeWriteOutcome> {
  const data = await invokePractice(buildPracticeAssignmentBody(intent, eventKey));
  try {
    const { replay } = parsePracticeAssignmentResult(data, intent, eventKey);
    return { status: "recorded", replay };
  } catch {
    return { status: "unreadable" };
  }
}
